import { type ReactElement } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface LamaParticipationChartPoint {
  readonly label: string;
  /** Coverage as a 0–100 percentage. */
  readonly participation: number;
  /** Numerator and denominator, when the resolver supplied them. Optional so an
   *  older resolver's payload still renders (PB-191 Task 20). */
  readonly submitted?: number;
  readonly total?: number;
  /** The wave's own date (`YYYY-MM-DD`), for the tooltip — see the trend chart. */
  readonly date?: string;
}

/**
 * Participation (coverage) per wave — a 0–100% bar per wave. Ported from
 * lama-feature's `LamaReportParticipationChart` for the dashboard widget body
 * (data-blind). Keeps the `role="img"` + data-derived aria summary pattern;
 * animation disabled for deterministic tests.
 */
export function LamaParticipationChart({
  data,
  height = 220,
}: {
  /** One bar per wave (oldest → newest). */
  readonly data: readonly LamaParticipationChartPoint[];
  readonly height?: number;
}): ReactElement {
  const latest = data.length ? data[data.length - 1] : null;
  const dates = new Map<string, string>();
  for (const point of data) {
    if (point.date !== undefined) dates.set(point.label, point.date);
  }
  /* The aria summary carries the DENOMINATOR when there is one, so a screen
     reader gets the same judgement a sighted reader gets from the text above the
     chart — a percentage alone is not an honest figure (Task 20). */
  const latestDetail =
    latest && latest.submitted !== undefined && latest.total !== undefined
      ? `; latest ${latest.participation}% coverage, ${latest.submitted} of ${latest.total} responses.`
      : latest
        ? `; latest ${latest.participation}% coverage.`
        : ".";
  const summary =
    `Participation by wave across ${data.length} wave${
      data.length === 1 ? "" : "s"
    }` + latestDetail;

  return (
    <div role="img" aria-label={summary} className="w-full" style={{ height }}>
      <div aria-hidden="true" className="h-full w-full">
        {/* Kept hand-built: recharts chart marks, which no design-system part draws. */}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={[...data]}
            /* `left: 0`, not `-16`: a `100%` tick is about 26px at 11px and
               the negative gutter clipped it against the card's padding. */
            margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
            /* Three waves across a wide card left three 40px sticks stranded in
               the middle of 360px bands. Capping the GAP rather than the bar
               keeps them proportionate at every card width. */
            barCategoryGap="28%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--pb-border))"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              tickLine={false}
              axisLine={{ stroke: "hsl(var(--pb-border))" }}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              tickLine={false}
              axisLine={false}
              width={40}
              unit="%"
            />
            <RechartsTooltip
              /* The hover band was `--pb-card`: the SAME token as the card the
                 chart sits on and as the tooltip itself, so hovering a bar
                 painted card colour over card colour and gave no feedback at
                 all. */
              cursor={{ fill: "hsl(var(--pb-card-elevated))" }}
              contentStyle={{
                fontSize: 12,
                /* `backgroundColor`, not the `background` shorthand — see the
                   maturity trend chart. */
                backgroundColor: "hsl(var(--pb-card))",
                border: "1px solid hsl(var(--pb-border))",
                borderRadius: 8,
                color: "hsl(var(--pb-text))",
              }}
              itemStyle={{ color: "hsl(var(--pb-text))" }}
              labelStyle={{ color: "hsl(var(--pb-text-muted))", fontSize: 11 }}
              labelFormatter={(label: unknown) => {
                const code = String(label);
                const date = dates.get(code);
                return date ? `${code} · ${date}` : code;
              }}
              /* The tooltip shows the fraction too, so hovering a bar answers
                 "of how many" without leaving the chart. */
              formatter={(value: number, _name, entry) => {
                const point = entry?.payload as
                  | LamaParticipationChartPoint
                  | undefined;
                const detail =
                  point?.submitted !== undefined && point.total !== undefined
                    ? `${value}% (${point.submitted} of ${point.total})`
                    : `${value}%`;
                return [detail, "Coverage"];
              }}
            />
            <Bar
              dataKey="participation"
              fill="hsl(var(--pb-cyan))"
              radius={[3, 3, 0, 0]}
              maxBarSize={40}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
